import React, { useState, useRef } from "react";
import { toast } from "react-hot-toast";
import {
  getFirestore,
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "../../firebase";
import { useAuth } from "../../contexts/AuthContext";
import {
  FileText,
  CheckCircle,
  Upload,
  Briefcase,
  GraduationCap,
  Clock,
  DollarSign,
  Building2,
  MapPin,
  Image as ImageIcon
} from "lucide-react";

const JDUpload = () => {
  const { user } = useAuth();


  const [step, setStep] = useState("input");
  const [jobDescription, setJobDescription] = useState("");

  // New state for additional fields
  const [jobTitle, setJobTitle] = useState("");
  const [education, setEducation] = useState("");
  const [experience, setExperience] = useState("");
  const [jobPosition, setJobPosition] = useState("");
  const [showSalary, setShowSalary] = useState(false);
  const [salary, setSalary] = useState("");

  // Company Details
  const [companyName, setCompanyName] = useState(user?.company || user?.name || "");
  const [companyLogo, setCompanyLogo] = useState(user?.avatar || "");
  const [companyAddress, setCompanyAddress] = useState("");
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);

  const [saving, setSaving] = useState(false);

  const handleProcess = () => {
    if (!jobDescription.trim()) return;
    setStep("preview");
  };

  const handlePublish = async () => {
    if (!user) {
      toast.error("Please sign in again");
      return;
    }
    if (!jobTitle || !education || !experience || !jobPosition) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (showSalary && !salary) {
      toast.error("Please enter a minimum salary.");
      return;
    }

    try {
      setSaving(true);
      const db = getFirestore(firebaseApp);

      const jobData = {
        hrId: user.id,
        hrEmail: user.email,
        jobTitle,
        jobDescription,
        education,
        experience: parseInt(experience, 10),
        jobPosition,
        experience: parseInt(experience, 10),
        jobPosition,
        companyName,
        companyLogo,
        companyAddress,
        createdAt: serverTimestamp(),
      };

      if (showSalary && salary) {
        jobData.salary = parseInt(salary, 10);
      }

      await addDoc(collection(db, "hrJobs"), jobData);
      toast.success("Job description published successfully!");
      setStep("complete");
    } catch (err) {
      console.error(err);
      toast.error("Failed to publish job. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (step === "complete") {
    return (
      <div className="max-w-2xl mx-auto py-20 text-center space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Job Published!</h1>
        <p className="text-gray-600">
          You can now generate tests or upload another JD.
        </p>
        <button
          onClick={() => {
            setJobDescription("");
            setJobTitle("");
            setEducation("");
            setExperience("");
            setJobPosition("");
            setJobPosition("");
            setShowSalary(false);
            setSalary("");
            setCompanyAddress("");
            setStep("input");
          }}
          className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Upload Another JD
        </button>
      </div>
    );
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    setUploadingLogo(true);
    try {
      const storage = getStorage(firebaseApp);
      const storageRef = ref(storage, `company-logos/${user.id}/${Date.now()}`);

      // Upload with timeout race condition
      const uploadPromise = uploadBytes(storageRef, file);
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Upload timed out")), 20000)
      );

      await Promise.race([uploadPromise, timeoutPromise]);

      const downloadURL = await getDownloadURL(storageRef);
      setCompanyLogo(downloadURL);
      toast.success("Logo uploaded successfully");
    } catch (error) {
      console.error("Error uploading logo:", error);
      toast.error(error.message === "Upload timed out" ? "Upload timed out. Please check your connection." : "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  if (step === "preview") {
    const isFormValid = jobTitle && education && experience && jobPosition && companyName;

    return (
      <div className="max-w-6xl mx-auto space-y-6 py-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Review Details</h1>
            <p className="text-gray-600 text-sm">Verify extracted information before publishing</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setStep("input")}
              className="px-4 py-2 border border-gray-200 text-gray-600 font-medium rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-all text-sm"
            >
              Back
            </button>
            <button
              onClick={handlePublish}
              disabled={saving || !isFormValid}
              className="px-6 py-2 bg-gradient-to-r from-red-700 to-red-900 text-white font-semibold rounded-lg hover:from-red-800 hover:to-red-950 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              {saving ? "Publishing..." : "Publish Job"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start h-[calc(100vh-200px)] min-h-[500px]">
          {/* Left Column: JD Textarea */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 h-full flex flex-col">
            <label className="block text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              Job Description
            </label>
            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              className="flex-1 w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none resize-none text-gray-700 leading-relaxed text-sm"
            />
          </div>

          {/* Right Column: Form Fields */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 h-full overflow-y-auto">
            {/* Company Details Section */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2 border-b border-gray-100 pb-2">
                <Building2 className="w-5 h-5 text-red-600" />
                Company Details
              </h3>
              <div className="space-y-4">
                {/* Logo Upload */}
                <div className="flex justify-center mb-4">
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="relative w-24 h-24 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center cursor-pointer hover:border-red-500 hover:bg-gray-50 transition-all group overflow-hidden"
                  >
                    {companyLogo ? (
                      <>
                        <img src={companyLogo} alt="Logo" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity z-10">
                          <Upload className="w-6 h-6 text-white" />
                        </div>
                      </>
                    ) : (
                      <div className="flex flex-col items-center text-gray-400 group-hover:text-red-500">
                        <ImageIcon className="w-8 h-8 mb-1" />
                        <span className="text-[10px] font-medium">Upload Logo</span>
                      </div>
                    )}
                    {uploadingLogo && (
                      <div className="absolute inset-0 bg-black/50 z-20 flex items-center justify-center backdrop-blur-sm">
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleLogoUpload}
                    accept="image/*"
                    className="hidden"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5">Company Name</label>
                  <input
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. Acme Corp"
                    className="block w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    Address
                  </label>
                  <input
                    type="text"
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder="e.g. 123 Tech Street, Silicon Valley"
                    className="block w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none text-sm"
                  />
                </div>
              </div>
            </div>

            <h3 className="font-semibold text-gray-900 mb-6 flex items-center gap-2 border-b border-gray-100 pb-4">
              <Briefcase className="w-5 h-5 text-red-600" />
              Job Details
            </h3>

            <div className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Job Title</label>
                <input
                  type="text"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                  placeholder="e.g., Senior Frontend Developer"
                  className="block w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-2">
                  <GraduationCap className="w-4 h-4 text-gray-400" />
                  Minimum Education
                </label>
                <input
                  type="text"
                  value={education}
                  onChange={(e) => setEducation(e.target.value)}
                  placeholder="e.g. Bachelor's Degree in Computer Science"
                  className="block w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-400" />
                  Years of Experience
                </label>
                <input
                  type="number"
                  value={experience}
                  onChange={(e) => setExperience(e.target.value)}
                  placeholder="e.g., 5"
                  className="block w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-gray-400" />
                  Job Position
                </label>
                <select
                  value={jobPosition}
                  onChange={(e) => setJobPosition(e.target.value)}
                  className="block w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none cursor-pointer bg-white text-sm"
                >
                  <option value="" disabled>Select...</option>
                  <option value="internship">Internship</option>
                  <option value="part-time">Part-time</option>
                  <option value="full-time">Full-time</option>
                  <option value="contract">Contract</option>
                </select>
              </div>

              <div className="pt-2">
                <div className="flex items-center mb-2">
                  <input
                    id="show-salary"
                    type="checkbox"
                    checked={showSalary}
                    onChange={(e) => setShowSalary(e.target.checked)}
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label htmlFor="show-salary" className="ml-2 block text-sm font-semibold text-gray-700 cursor-pointer flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-gray-400" />
                    Mention minimum salary
                  </label>
                </div>
                {showSalary && (
                  <input
                    type="number"
                    value={salary}
                    onChange={(e) => setSalary(e.target.value)}
                    placeholder="e.g., 80000 (per annum)"
                    className="block w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all outline-none animate-in fade-in slide-in-from-top-2 text-sm"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Input step
  return (
    <div className="max-w-6xl mx-auto py-10 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
        {/* Left Column: Info & Features */}
        <div className="space-y-8">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4 leading-tight">
              Upload <span className="text-red-700">Job Description</span>
            </h1>
            <p className="text-lg text-gray-600 leading-relaxed">
              Our intelligent system analyzes your job description to extract key requirements and automatically generates relevant skill assessments.
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center shrink-0">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Smart Analysis</h3>
                <p className="text-sm text-gray-600">Intelligent system extracts skills, requirements, and experience levels automatically.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center shrink-0">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Auto Test Generation</h3>
                <p className="text-sm text-gray-600">Automatically generate skill-based tests from job requirements.</p>
              </div>
            </div>

            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center shrink-0">
                <Upload className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-1">Instant Matching</h3>
                <p className="text-sm text-gray-600">Match with qualified candidates immediately after publishing.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Upload Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-bl-full -mr-8 -mt-8 opacity-50 pointer-events-none" />

          <div className="space-y-6 relative">
            <div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Paste Job Description
              </h3>
              <p className="text-gray-500 text-sm">
                Copy text from your JD document and paste below
              </p>
            </div>

            <textarea
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              placeholder="e.g. We are looking for a Senior React Developer with 5+ years of experience..."
              className="w-full h-80 px-6 py-5 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none transition-all shadow-inner text-gray-700 leading-relaxed hover:border-gray-300 bg-gray-50/30"
            />

            <button
              onClick={handleProcess}
              disabled={!jobDescription.trim()}
              className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-red-700 to-red-900 text-white font-semibold rounded-xl hover:from-red-800 hover:to-red-950 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-5 h-5 mr-2" />
              Process Job Description
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JDUpload;
